import { useEffect, useRef, useState } from 'react';

const useIntersect = (
  elementRef,
  {
    root = null,
    rootMargin,
    threshold = 0,
    onlyOnce = false,
  }: {
    root?: Element | null;
    rootMargin: string;
    threshold: number;
    onlyOnce?: boolean;
  },
): IntersectionObserverEntry | undefined => {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();

  const observerNode = useRef(null);
  const updateEntry = ([intersectionEntry]: IntersectionObserverEntry[]): void => {
    setEntry(intersectionEntry);
  };

  useEffect(() => {
    if (entry?.isIntersecting && onlyOnce) {
      setEntry(undefined);
      return undefined;
    }

    const hasIOSupport = !!window.IntersectionObserver;
    if (!hasIOSupport) {
      return undefined;
    }

    let observer: IntersectionObserver | null = null;

    const setupObserver = () => {
      const node = elementRef?.current;
      if (!node) {
        return false;
      }

      observer?.disconnect();
      observer = new IntersectionObserver(updateEntry, { threshold, root, rootMargin });
      observer.observe(node);
      return true;
    };

    let timeoutId: number | undefined;
    if (!setupObserver()) {
      timeoutId = window.setTimeout(setupObserver, 0);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      observer?.disconnect();
    };
  }, [observerNode, root, rootMargin, threshold]);

  return entry;
};

export default useIntersect;
